import { themeConfig } from './theme-config';

export interface IFärg {
    hexkod: string;
    namn: string;
}

export interface IFärgKombo {
    primary: IFärg;
    secondary: IFärg;
}

/**
 * Varumärkesfärgerna härleds ur {@link themeConfig} — de bodde tidigare här som
 * egna hexkoder, parallellt med MUI-paletten.
 *
 * Det var två sanningar om samma sak: `core/palette.ts` hämtade sitt `primary.main`
 * härifrån (turkos) men resten av rampen från Minimal-mallen (grön), medan
 * `themeConfig.palette.primary` inte lästes av någon. Att byta appens färg på ett
 * ställe ändrade därför bara halva appen.
 *
 * Nu finns färgen på ett ställe: `theme-config.ts`. Både MUI-paletten och de
 * komponenter som ritar accenter för hand läser ur samma ramp.
 *
 * `Kombinationer` och `ChipFärger` längre ned är avsiktligt kvar som egna
 * hexkoder — de är kategoriska skalor för diagram och chips, där poängen är att
 * färgerna ska gå att skilja åt, inte att de ska följa varumärket.
 */
const PrimaryBase: IFärg = { hexkod: themeConfig.palette.primary.main, namn: 'Primary Base' };
const SecondaryBase: IFärg = { hexkod: themeConfig.palette.secondary.main, namn: 'Secondary Base' };

const PrimarySkala: IFärg[] = [
    { hexkod: themeConfig.palette.primary.light, namn: 'Primary Light' },
    { hexkod: themeConfig.palette.primary.main, namn: 'Primary Main' },
    { hexkod: themeConfig.palette.primary.dark, namn: 'Primary Dark' },
];

const SecondarySkala: IFärg[] = [
    { hexkod: themeConfig.palette.secondary.light, namn: 'Secondary Light' },
    { hexkod: themeConfig.palette.secondary.main, namn: 'Secondary Main' },
    { hexkod: themeConfig.palette.secondary.dark, namn: 'Secondary Dark' },
];

// Green fading to light/white
export const GrönFadeMotLjus: string[] = [
    "#266960",
    "#297970",
    "#2c8980",
    "#30968d",
    "#3fa69f",
    "#5bb6b0",
    "#87cbc7"
];

// Light green fade #cef7e5, #b0f1d6, #8bedc5, #68e6b8, #33e1aa
export const LjusGrönFade: string[] = [
    "#cef7e5", // Originally #c2f5df
    "#b0f1d6", // Originally #9ceecc
    "#8bedc5", // Originally #6ee8b7
    "#68e6b8", // Originally #42e0a6
    "#33e1aa"  // Originally #00d995
];

// Blue fading to light
export const BlåttFadeMotLjus: string[] = [
    "#43359a",
    "#4a3fa6",
    "#534ab2",
    "#5952bd",
    "#6f6dc7",
    "#8788d2"
];

export const NyttFärgTema = {
    PrimaryBase,
    SecondaryBase,
    PrimarySkala,
    SecondarySkala,
    Kombinationer: [
        {
            primary: { hexkod: '#20369e', namn: 'Dark blue' },
            secondary: { hexkod: '#9ea9e0', namn: 'Light blue' }
        },
        {
            primary: { hexkod: '#7c2aa7', namn: 'Dark purple' },
            secondary: { hexkod: '#cf97db', namn: 'Light purple' }
        },
        {
            primary: { hexkod: '#008662', namn: 'Dark purple' },
            secondary: { hexkod: '#71d4be', namn: 'Light purple' }
        },

        {
            primary: { hexkod: '#5ee7df', namn: 'Teal' },
            secondary: { hexkod: '#b490ca', namn: 'Purple' }
        },
        {
            primary: PrimaryBase,
            secondary: SecondaryBase
        },
        {
            primary: { hexkod: '#2f36a0', namn: 'Light Gray' },
            secondary: { hexkod: '#92d9f2', namn: 'Light Blue' }
        },
        {
            primary: { hexkod: '#c3cfe2', namn: 'Light Gray' },
            secondary: { hexkod: '#c3cfe2', namn: 'Light Blue' }
        },
        {
            primary: { hexkod: '#616dcc', namn: 'Light Gray' },
            secondary: { hexkod: '#5631ab', namn: 'Light Blue' }
        },
        {
            primary: { hexkod: '#f6d365', namn: 'Sunny Yellow' },
            secondary: { hexkod: '#fda085', namn: 'Warm Orange' }
        },
        {
            primary: { hexkod: '#4facfe', namn: 'Blue' },
            secondary: { hexkod: '#00f2fe', namn: 'Cyan' }
        },
        {
            primary: { hexkod: '#43e97b', namn: 'Green' },
            secondary: { hexkod: '#38f9d7', namn: 'Mint' }
        },
        {
            primary: { hexkod: '#f093fb', namn: 'Pink' },
            secondary: { hexkod: '#f5576c', namn: 'Red' }
        }
    ] as IFärgKombo[]
};

export const ChipFärger = [
    '#20369e', // 0: Dark Blue
    '#7c2aa7', // 1: Dark Purple
    '#008662', // 2: Dark Green
    '#5ee7df', // 3: Teal
    '#5aa9af', // 4: Primary Base
    '#2f36a0', // 5: Blue
    '#9370c8', // 6: Purple (Annan)
    '#616dcc', // 7: Medium Blue
    '#c8ce28', // 8: Yellow
    '#4facfe', // 9: Bright Blue
    '#83cfc8', // 10: Specific Green (Kortköp)
    '#f093fb', // 11: Pink/Purple
];
